import { RoundedBoxGeometry } from "three/examples/jsm/geometries/RoundedBoxGeometry.js";
import * as THREE from "three";
import { WorkshopArt } from "../art/workshop";

/**
 * Sorting's box row (Master Spec §24, §46; Phase 05 "Sorting Foundation").
 * Unlike Searching's Box.ts, every value is visible from the start (§24's
 * "VALUES ARE VISIBLE" rule) — there is no hidden/checked/found reveal
 * lifecycle here, only "default" / "comparing" / "sorted" highlighting of
 * values the player already knows. Kept as its own entity module rather
 * than folded into Box.ts because the two domains' visual contracts
 * genuinely diverge (hidden-information reveal vs. already-visible
 * reordering) — the same reasoning that keeps GameRuntime's Searching and
 * Sorting code paths separate (see its `stage` field) rather than forcing
 * a shared abstraction ahead of schedule. Shares geometry/materials
 * across all boxes per §46 — no per-box duplicates.
 */
export type SortBoxVisualState = "default" | "comparing" | "candidate" | "sorted";

export interface SortBoxRowHandles {
  group: THREE.Group;
  bounds: { minX: number; maxX: number };
  getNearestIndex: (x: number) => number;
  getBoxWorldX: (index: number) => number;
  setNearestIndex: (index: number | null) => void;
  setValues: (values: number[]) => void;
  update: (dt:number) => void;
  setValue: (index: number, value: number) => void;
  setState: (index: number, state: SortBoxVisualState) => void;
  dispose: () => void;
}

const BOX_SIZE = 0.9;
// Cool teal base — deliberately distinct from Searching's warm orange
// (Box.ts) so a player can tell which stage they're in at a glance.
const DEFAULT_COLOR = 0xbf844b;
const NEAREST_COLOR = 0x8fe0ff;
const COMPARING_COLOR = 0xf4c542;
// Selection Sort's "current best-so-far candidate" highlight (Master Spec
// §26) — deliberately a different hue from COMPARING_COLOR so a player can
// tell "this is the tracked candidate" apart from "these two are being
// compared right now" at a glance, even though both can be visible in the
// same frame (the candidate box and the scan box are compared every step).
const CANDIDATE_COLOR = 0xb388ff;
const SORTED_COLOR = 0x4caf6d;
const LABEL_TEXT_COLOR = "#0b2733";

function createValueLabel(): { sprite: THREE.Mesh; setValue: (value: number) => void; dispose: () => void } {
  const canvas = document.createElement("canvas");
  canvas.width = 128;
  canvas.height = 128;
  const context = canvas.getContext("2d");

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace=THREE.SRGBColorSpace;
  const material = new THREE.MeshBasicMaterial({ map: texture, transparent: true });
  const labelGeometry=new THREE.PlaneGeometry(1,1);
  const sprite = new THREE.Mesh(labelGeometry,material);
  sprite.scale.set(0.6, 0.6, 1);

  function setValue(value: number): void {
    if (!context) return;
    context.clearRect(0, 0, canvas.width, canvas.height);
    context.fillStyle = "#fff0d6";
    context.fillRect(6,6,116,116);
    context.fillStyle = LABEL_TEXT_COLOR;
    context.font = "bold 64px sans-serif";
    context.textAlign = "center";
    context.textBaseline = "middle";
    context.fillText(String(value), canvas.width / 2, canvas.height / 2);
    texture.needsUpdate = true;
  }

  function dispose(): void {
    labelGeometry.dispose();
    texture.dispose();
    material.dispose();
  }

  return { sprite, setValue, dispose };
}

export function createSortBoxRow(initialValues: number[], spacing: number): SortBoxRowHandles {
  const count = initialValues.length;
  const group = new THREE.Group();
  const art = new WorkshopArt();
  const width = (count-1)*spacing+1.5;
  art.box(group,0x33465e,[width,.3,1.3],[0,.15,0]);
  art.box(group,0x606b74,[width-.1,.09,1.1],[0,.34,0]);
  for(let x=-width/2+.2;x<width/2;x+=.33) art.box(group,0x859298,[.2,.04,1],[x,.405,0]);
  for(const z of [-.63,.63]) art.box(group,0xf5bd40,[width,.08,.08],[0,.35,z]);
  const geometry = new RoundedBoxGeometry(BOX_SIZE, BOX_SIZE, BOX_SIZE, 2, .035);
  const defaultMaterial = new THREE.MeshStandardMaterial({ color: DEFAULT_COLOR, roughness: 0.6 });
  const nearestMaterial = new THREE.MeshStandardMaterial({ color: NEAREST_COLOR, roughness: 0.4 });
  const comparingMaterial = new THREE.MeshStandardMaterial({ color: COMPARING_COLOR, roughness: 0.4 });
  const candidateMaterial = new THREE.MeshStandardMaterial({ color: CANDIDATE_COLOR, roughness: 0.4 });
  const sortedMaterial = new THREE.MeshStandardMaterial({ color: SORTED_COLOR, roughness: 0.4 });

  const meshes: THREE.Mesh[] = [];
  const labels: ReturnType<typeof createValueLabel>[] = [];
  const states: SortBoxVisualState[] = new Array(count).fill("default");
  let currentValues = [...initialValues];
  let animationTime = 1;
  let starts: number[] = [];
  let startHeights:number[]=[];
  let startAngles:number[]=[];
  let nearestIndex: number | null = null;

  const startX = -((count - 1) * spacing) / 2;

  for (let i = 0; i < count; i += 1) {
    const mesh = new THREE.Mesh(geometry, defaultMaterial);
    mesh.position.set(startX + i * spacing, BOX_SIZE / 2 + .43, 0);
    mesh.name="parcel";
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    group.add(mesh);
    meshes.push(mesh);

    art.box(mesh,0xeac18d,[.16,.024,.91],[0,.462,0]);
    for(const x of [-.38,.38]) art.box(mesh,0xe9b474,[.075,.88,.03],[x,0,.46]);
    art.label(group,String(i+1),.4,.22,[startX+i*spacing,.18,.665],"#29405e","#ffffff");
    const label = createValueLabel();
    label.setValue(initialValues[i]!);
    label.sprite.position.set(0,.07,.5);
    mesh.add(label.sprite);
    labels.push(label);
  }

  function getBoxWorldX(index: number): number {
    return startX + index * spacing;
  }

  function getNearestIndex(x: number): number {
    let nearest = 0;
    let smallestDistance = Number.POSITIVE_INFINITY;
    for (let i = 0; i < count; i += 1) {
      const distance = Math.abs(getBoxWorldX(i) - x);
      if (distance < smallestDistance) {
        smallestDistance = distance;
        nearest = i;
      }
    }
    return nearest;
  }

  // Precedence: sorted > candidate > comparing > nearest > default. The
  // tracked candidate stays visually stable across an entire scan round
  // (Master Spec §26) even while a COMPARE event briefly flashes the box
  // it's being measured against as "comparing" — so candidate must win
  // over comparing, otherwise the candidate's own highlight would blink
  // off every time it gets compared against the next scan index.
  function applyMaterial(index: number): void {
    const mesh = meshes[index];
    if (!mesh) return;
    if (states[index] === "sorted") {
      mesh.material = sortedMaterial;
      return;
    }
    if (states[index] === "candidate") {
      mesh.material = candidateMaterial;
      return;
    }
    if (states[index] === "comparing") {
      mesh.material = comparingMaterial;
      return;
    }
    mesh.material = nearestIndex === index ? nearestMaterial : defaultMaterial;
  }

  function setNearestIndex(index: number | null): void {
    const previous = nearestIndex;
    nearestIndex = index;
    if (previous !== null) applyMaterial(previous);
    if (index !== null) applyMaterial(index);
  }

  function setValues(values: number[]): void {
    if(values.every((v,i)=>v===currentValues[i])) return;
    const available = currentValues.map((value,i)=>({value,mesh:meshes[i]!,label:labels[i]!,used:false}));
    values.forEach((value,i)=>{
      const match=available.find(item=>!item.used && item.value===value)!;
      if(match){ match.used=true; meshes[i]=match.mesh; labels[i]=match.label; }
      else labels[i]?.setValue(value);
    });
    starts=meshes.map(mesh=>mesh.position.x);startHeights=meshes.map(mesh=>mesh.position.y);startAngles=meshes.map(mesh=>mesh.rotation.z); animationTime=0; currentValues=[...values];
  }
  function update(dt:number): void {
    if(animationTime>=1) return;
    animationTime=Math.min(1,animationTime+dt*2.3);
    const t=animationTime*animationTime*(3-2*animationTime);
    meshes.forEach((mesh,i)=>{
      mesh.rotation.z=THREE.MathUtils.lerp(startAngles[i]!,0,t)+Math.sin(animationTime*Math.PI)*Math.sign(starts[i]!-getBoxWorldX(i))*.09;
      mesh.position.x=THREE.MathUtils.lerp(starts[i]!,getBoxWorldX(i),t);
      mesh.position.y=THREE.MathUtils.lerp(startHeights[i]!,BOX_SIZE/2+.43,t)+(Math.abs(starts[i]!-getBoxWorldX(i))>.01?Math.sin(animationTime*Math.PI)*(starts[i]!>getBoxWorldX(i)?.6:.25):0);
    });
  }
  function setValue(index: number, value: number): void {
    if (!labels[index]) return;
    labels[index].setValue(value);
  }

  function setState(index: number, state: SortBoxVisualState): void {
    if (!meshes[index]) return;
    states[index] = state;
    applyMaterial(index);
  }

  function dispose(): void {
    art.dispose();
    geometry.dispose();
    defaultMaterial.dispose();
    nearestMaterial.dispose();
    comparingMaterial.dispose();
    candidateMaterial.dispose();
    sortedMaterial.dispose();
    labels.forEach((label) => label.dispose());
  }

  const bounds = { minX: startX, maxX: startX + (count - 1) * spacing };

  return { group, bounds, getNearestIndex, getBoxWorldX, setNearestIndex, setValues, update, setValue, setState, dispose };
}
